/**
 * The §4 auth seam for an axios instance, mirroring the admin dashboard's tested
 * fetch seam (vivo-admin-web http.ts) but expressed as axios interceptors for
 * Vue2's client:
 *
 *  - request: attach `Authorization: Bearer <token>` (read fresh from an injected
 *    getter) + `Accept-Language`.
 *  - response: on a 401 for a protected call, silently refresh ONCE via the
 *    httpOnly refresh cookie, then replay the original request. Three invariants
 *    prevent recursion / storms: a per-request `_retry` flag (single retry),
 *    an `/auth/*` skip (never refresh the refresh), and a per-instance
 *    single-flight promise (concurrent 401s share one refresh). A failed refresh
 *    calls `onAuthLost` (clear session + bounce to login).
 *
 * Kept as plain CommonJS so node:test can drive it with a mock adapter — the
 * refresh behaviour is unit-proven, not left to the e2e.
 */

// A 403 (tenant/role-forbidden) previously surfaced to the caller exactly like
// any other error (raw backend body, or nothing at all) — indistinguishable
// from a generic failure. Attach a clear, localized message onto the error
// (mirroring the admin dashboard's fetch seam) WITHOUT touching the 401
// retry/refresh branch below, which is keyed on `status === 401` and never
// sees a 403.
var FORBIDDEN_MESSAGE = {
  ar: 'ليس لديك صلاحية لهذا الإجراء',
  en: 'You do not have permission to perform this action',
};

function forbiddenMessage(lang) {
  var l = String(lang || '').toLowerCase();
  return l.indexOf('ar') === 0 ? FORBIDDEN_MESSAGE.ar : FORBIDDEN_MESSAGE.en;
}

function installAuthInterceptors(instance, deps) {
  deps = deps || {};
  var getToken = deps.getToken || function () { return undefined; };
  var getLang = deps.getLang || function () { return 'ar-EG'; };
  var onRefresh = deps.onRefresh || null; // () => Promise<boolean>
  var onAuthLost = deps.onAuthLost || function () {};

  var refreshPromise = null; // per-instance single-flight

  instance.interceptors.request.use(function (config) {
    var token = getToken();
    if (token && config.headers && config.headers.set) {
      config.headers.set('Authorization', 'Bearer ' + token);
    }
    var lang = getLang();
    if (lang && config.headers && config.headers.set) {
      config.headers.set('Accept-Language', lang);
    }
    return config;
  });

  function isAuthPath(url) {
    return String(url || '').indexOf('/auth/') !== -1;
  }

  function refreshOnce() {
    if (!refreshPromise) {
      refreshPromise = Promise.resolve()
        .then(function () { return onRefresh ? onRefresh() : false; })
        .then(function (ok) { refreshPromise = null; return ok; })
        .catch(function () { refreshPromise = null; return false; });
    }
    return refreshPromise;
  }

  instance.interceptors.response.use(
    function (res) { return res; },
    function (error) {
      var config = (error && error.config) || {};
      var status = error && error.response && error.response.status;
      if (status === 401 && onRefresh && !config._retry && !isAuthPath(config.url)) {
        config._retry = true;
        return refreshOnce().then(function (ok) {
          if (ok) {
            return instance(config);
          }
          onAuthLost();
          return Promise.reject(error);
        });
      }
      if (status === 403 && error && error.response) {
        var data = error.response.data;
        if (!data || typeof data !== 'object') {
          data = {};
          error.response.data = data;
        }
        data.message = forbiddenMessage(getLang());
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

module.exports = { installAuthInterceptors: installAuthInterceptors };
